<template>
    <header class="header">
        <div class="container">
            <div class="row">
                <router-link :to="{ name: 'home' }">
                    <img :src="logo" alt="Baltic Expo" class="header__logo">
                </router-link>
                <div class="header__text" v-if="$route.name === 'home'">{{ t('header.reserve') }}</div>
                <div class="header__text" v-else-if="$route.name === 'booking'">{{ t('header.finalize') }}</div>
                <app-button @click="logout" class="header__logout" v-if="$route.name === 'admin'">Log Out</app-button>
                <div class="header__lang" v-if="showLangSwitcher" @click.stop>
                    <button class="header__lang-current" @click="toggleDropdown">
                        <img class="header__lang-flag" :src="currentLang.flagSrc" :alt="currentLang.label">
                        <span class="header__lang-label">{{ currentLang.label }}</span>
                        <svg class="header__lang-arrow" :class="{ open: dropdownOpen }" width="10" height="6" viewBox="0 0 10 6" fill="none">
                            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <div class="header__lang-dropdown" v-if="dropdownOpen">
                        <button
                            v-for="lang in langs"
                            :key="lang.code"
                            class="header__lang-option"
                            :class="{ active: locale === lang.code }"
                            @click="setLocale(lang.code)"
                        >
                            <img class="header__lang-flag" :src="lang.flagSrc" :alt="lang.label">
                            <span>{{ lang.label }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>

<script setup>
import logo from '../assets/logo.svg'
import AppButton from './Button.vue'
import { useUserStore } from '../store/user'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { computed, ref, onMounted, onUnmounted } from 'vue'

const { t, locale } = useI18n()
const route = useRoute()
const userStore = useUserStore()

const dropdownOpen = ref(false)

const langs = [
    { code: 'lv', label: 'Latviešu', flagSrc: 'https://flagcdn.com/w40/lv.png' },
    { code: 'lt', label: 'Lietuvių', flagSrc: 'https://flagcdn.com/w40/lt.png' },
    { code: 'et', label: 'Eesti',    flagSrc: 'https://flagcdn.com/w40/ee.png' },
    { code: 'en', label: 'English',  flagSrc: 'https://flagcdn.com/w40/gb.png' },
]

const currentLang = computed(() => langs.find(l => l.code === locale.value) ?? langs[0])

const showLangSwitcher = computed(() =>
    ['home', 'booking', 'thank-you', 'landing'].includes(route.name) || route.path === '/test'
)

const toggleDropdown = () => {
    dropdownOpen.value = !dropdownOpen.value
}

const setLocale = (lang) => {
    locale.value = lang
    localStorage.setItem('locale', lang)
    dropdownOpen.value = false
}

const closeDropdown = () => {
    dropdownOpen.value = false
}

const logout = async () => {
    try {
        await userStore.logOut()
        router.push({ name: 'login' })
    }
    catch (err) {
        console.log(err)
    }
}

onMounted(() => document.addEventListener('click', closeDropdown))
onUnmounted(() => document.removeEventListener('click', closeDropdown))
</script>

<style lang="sass" scoped>
.header
    .row
        align-items: center
        height: 59px
        gap: 16px

    &__logo
        height: 35px

    &__text
        font-size: 14px
        font-weight: 600
        padding: 0 0 4px 0

    &__logout
        margin-left: auto

    &__lang
        margin-left: auto
        position: relative

    &__lang-current
        display: flex
        align-items: center
        gap: 6px
        background: none
        border: none
        cursor: pointer
        font-size: 13px
        font-weight: 600
        color: #09A59A
        padding: 4px 6px
        font-family: inherit
        border-radius: 6px
        transition: background 0.15s

        &:hover
            background: #f0faf9

    &__lang-flag
        width: 20px
        height: auto
        border-radius: 2px
        display: block
        flex-shrink: 0

    &__lang-label
        @media only screen and (max-width: 400px)
            display: none

    &__lang-arrow
        color: #09A59A
        transition: transform 0.2s
        flex-shrink: 0

        &.open
            transform: rotate(180deg)

    &__lang-dropdown
        position: absolute
        top: calc(100% + 6px)
        right: 0
        background: #fff
        border: 1px solid #E5E7EB
        border-radius: 8px
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1)
        overflow: hidden
        min-width: 148px
        z-index: 100

    &__lang-option
        display: flex
        align-items: center
        gap: 8px
        width: 100%
        padding: 9px 14px
        background: none
        border: none
        cursor: pointer
        font-size: 13px
        font-weight: 500
        color: #374151
        font-family: inherit
        text-align: left
        transition: background 0.15s

        &:hover
            background: #f0faf9

        &.active
            color: #09A59A
            font-weight: 700
            background: #f0faf9
</style>
